import React, { useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useMapEvents } from 'react-leaflet';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';

const menuStyles = {
  position: 'absolute',
  zIndex: 700,
  minWidth: 220,
};

/**
 * Tour-tour specific map context menu. Allows builders to quickly pick a tool without
 * exposing the zone actions from the Responder sandbox.
 */
/**
 * tour map context menu.
 * @param {object} props
 * @param {boolean} props.showTourTools
 * @param {(next: boolean) => void} props.setShowTourTools
 * @param {boolean} props.showZoneAnchors
 * @param {(next: boolean) => void} props.setShowZoneAnchors
 * @returns {JSX.Element|null}
 */
export default function TourContextMenu({
  showTourTools,
  setShowTourTools,
  showZoneAnchors,
  setShowZoneAnchors,
}) {
  const { setMapTool } = useTours();
  const [menuState, setMenuState] = useState(null);

  const map = useMapEvents({
    contextmenu: (e) => {
      e.originalEvent?.preventDefault?.();
      const pt = map.latLngToContainerPoint(e.latlng);
      setMenuState({
        latlng: e.latlng,
        point: pt,
      });
    },
    click: (e) => {
      const target = e?.originalEvent?.target;
      if (target?.closest && target.closest('.tour-context-menu')) {
        return;
      }
      setMenuState(null);
    },
    keydown: (e) => {
      if (e.originalEvent?.key === 'Escape') {
        setMenuState(null);
      }
    },
  });

  const positionStyle = useMemo(() => {
    if (!menuState) return { display: 'none' };
    return { left: menuState.point.x, top: menuState.point.y };
  }, [menuState]);

  if (!menuState) return null;

  const handleCheckpointTool = (geometry) => {
    setMapTool?.({ mode: 'checkpoint', geometry });
    setMenuState(null);
  };

  const handleStartTool = () => {
    setMapTool?.({ mode: 'start' });
    setMenuState(null);
  };

  return (
    <Card
      className="tour-context-menu responder-map-context-menu border border-theme-strong shadow-lg bg-primary-shadow text-theme"
      style={{ ...menuStyles, ...positionStyle }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Card.Body className="p-2 d-flex flex-column gap-2">
        <div className="fw-semibold small text-uppercase text-theme-muted">Map actions</div>
        <div className="d-flex flex-column gap-1">
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-map-context-button responder-interactive-action"
            onClick={() => handleCheckpointTool('circle')}
          >
            Place circle checkpoint
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-map-context-button responder-interactive-action"
            onClick={() => handleCheckpointTool('polygon')}
          >
            Place polygon checkpoint
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-map-context-button responder-interactive-action"
            onClick={() => handleCheckpointTool('crossline')}
          >
            Place crossline checkpoint
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-map-context-button responder-interactive-action"
            onClick={handleStartTool}
          >
            Set start location
          </Button>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span className="small text-theme-muted">Show Tour tools</span>
          <Button
            size="sm"
            variant={showTourTools ? 'primary' : 'outline-secondary'}
            className={`responder-map-context-toggle responder-interactive-action ${showTourTools ? 'is-selected' : ''}`}
            onClick={() => {
              setShowTourTools?.(!showTourTools);
              setMenuState(null);
            }}
          >
            {showTourTools ? 'Hide' : 'Show'}
          </Button>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span className="small text-theme-muted">Show Zone Anchors</span>
          <Button
            size="sm"
            variant={showZoneAnchors ? 'primary' : 'outline-secondary'}
            className={`responder-map-context-toggle responder-interactive-action ${showZoneAnchors ? 'is-selected' : ''}`}
            onClick={() => {
              setShowZoneAnchors?.(!showZoneAnchors);
              setMenuState(null);
            }}
          >
            {showZoneAnchors ? 'Hide' : 'Show'}
          </Button>
        </div>
        <div className="small text-theme-muted">
          After selecting a tool, left-click on the map to place it.
        </div>
      </Card.Body>
    </Card>
  );
}
